/**
 * Catalog photo upload (Spec 016).
 *
 * File picker → client-side MIME + size guard → `presignCatalogPhoto`
 * → PUT to S3 → reports the public CloudFront URL back to the parent
 * form, which saves it on the material's `photo_url`.
 */

import { useRef, useState } from "react";

import { presignCatalogPhoto } from "@starz/shared/api/sdk.gen";

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB — matches the backend cap
const ALLOWED = ["image/jpeg", "image/png"] as const;

interface Props {
  /** Used in the S3 key prefix; required before upload. */
  materialId: string;
  /** Current photo_url, if any. */
  value: string | null;
  onChange: (photoUrl: string | null) => void;
}

export function CatalogPhotoUpload({ materialId, value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setError(null);

    if (!ALLOWED.includes(file.type as (typeof ALLOWED)[number])) {
      setError("Photo must be a JPEG or PNG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Photo exceeds the 8 MB limit.");
      return;
    }
    if (!materialId.trim()) {
      setError("Set the material ID before uploading a photo.");
      return;
    }

    setUploading(true);
    const presigned = await presignCatalogPhoto({
      mimeType: file.type as "image/jpeg" | "image/png",
      bytes: file.size,
      materialId: materialId.trim(),
    });
    if ("error" in presigned) {
      setUploading(false);
      setError(presigned.error.error.message);
      return;
    }

    const put = await fetch(presigned.data.upload_url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    setUploading(false);
    if (!put.ok) {
      setError(`Upload failed (${put.status}). Try again.`);
      return;
    }
    onChange(presigned.data.photo_url);
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Photo
      </label>
      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt="Material"
            className="h-16 w-16 rounded-lg border border-starz-gold/15 object-cover"
          />
        ) : (
          <div className="grid h-16 w-16 place-items-center rounded-lg border border-dashed border-starz-gold/30 bg-starz-cream text-xs text-warm-gray">
            none
          </div>
        )}
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="rounded-md border border-starz-gold/30 px-3 py-1.5 text-xs font-medium text-starz-navy hover:bg-starz-cream disabled:opacity-50"
          >
            {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-left text-xs text-warm-gray hover:text-starz-brick"
            >
              Remove
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          onChange={onPick}
          className="hidden"
        />
      </div>
      {error ? (
        <p className="border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
          {error}
        </p>
      ) : null}
    </div>
  );
}
